import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { PhysicalCountService } from '../../../api/physicalCountCompat';
import type { PhysicalCountParsePreviewResponse } from '../../../types/physicalCount.types';
import {
  extractErrorMessage,
  extractPhysicalCountPreviewErrorMessage,
} from '../../../utils/errorHelpers';

const UPLOAD_PARSE_MODE = 'single_column' as const;
const UPLOAD_NORMALIZATION = 'preserve_case' as const;

export interface PhysicalCountPanelState {
  locationId: string;
  sessionDate: string;
}

const getInitialState = (): PhysicalCountPanelState => ({
  locationId: '',
  sessionDate: new Date().toISOString().slice(0, 10),
});

export interface UsePhysicalCountPanelProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const usePhysicalCountPanel = ({ open, onClose, onSuccess }: UsePhysicalCountPanelProps) => {
  const totalSteps = 2;
  const [currentStep, setCurrentStep] = useState(1);
  const [state, setState] = useState<PhysicalCountPanelState>(getInitialState);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  const [uploadFile, setUploadFileState] = useState<File | null>(null);
  const [preview, setPreview] = useState<PhysicalCountParsePreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const previewRequestIdRef = useRef(0);

  useEffect(() => {
    if (open) {
      setState({
        locationId: '',
        sessionDate: new Date().toISOString().slice(0, 10),
      });
      setCurrentStep(1);
      setUploadFileState(null);
      setPreview(null);
      setPreviewLoading(false);
      setSaveError(null);
      setShowCloseConfirmation(false);
      previewRequestIdRef.current += 1;
      setFileInputKey(0);
    }
  }, [open]);

  useEffect(() => {
    setSaveError(null);
  }, [currentStep]);

  const updateState = useCallback((updates: Partial<PhysicalCountPanelState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const hasProgress = useMemo(
    () => state.locationId !== '' || uploadFile != null || preview != null || currentStep > 1,
    [currentStep, preview, state.locationId, uploadFile],
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleCloseClick = useCallback(() => {
    if (saving || previewLoading) return;
    if (hasProgress) {
      setShowCloseConfirmation(true);
      return;
    }
    handleClose();
  }, [handleClose, hasProgress, previewLoading, saving]);

  const handleConfirmClose = useCallback(() => {
    setShowCloseConfirmation(false);
    handleClose();
  }, [handleClose]);

  const handleCancelClose = useCallback(() => {
    setShowCloseConfirmation(false);
  }, []);

  const runPreview = useCallback(async (file: File, locationId: string) => {
    const requestId = ++previewRequestIdRef.current;
    setPreviewLoading(true);
    setSaveError(null);
    setPreview(null);
    try {
      const result = await PhysicalCountService.parsePreview(
        Number(locationId),
        file,
        UPLOAD_PARSE_MODE,
        UPLOAD_NORMALIZATION,
      );
      if (previewRequestIdRef.current !== requestId) return;
      setPreview(result);
    } catch (err: unknown) {
      if (previewRequestIdRef.current !== requestId) return;
      setPreview(null);
      setUploadFileState(null);
      setFileInputKey((k) => k + 1);
      setSaveError(extractPhysicalCountPreviewErrorMessage(err));
    } finally {
      if (previewRequestIdRef.current === requestId) {
        setPreviewLoading(false);
      }
    }
  }, []);

  const setUploadFile = useCallback(
    (file: File | null) => {
      setUploadFileState(file);
      setPreview(null);
      setSaveError(null);
      if (file != null && state.locationId !== '') {
        void runPreview(file, state.locationId);
      }
    },
    [state.locationId, runPreview],
  );

  const handleNextStep = useCallback(() => {
    if (currentStep === 1 && state.locationId !== '' && state.sessionDate !== '') {
      setCurrentStep(2);
      if (uploadFile != null) {
        void runPreview(uploadFile, state.locationId);
      }
    }
  }, [currentStep, state.locationId, state.sessionDate, uploadFile, runPreview]);

  const handlePreviousStep = useCallback(() => {
    if (currentStep === 2) {
      previewRequestIdRef.current += 1;
      setPreviewLoading(false);
      setCurrentStep(1);
      setUploadFileState(null);
      setPreview(null);
      setSaveError(null);
      setFileInputKey((k) => k + 1);
    }
  }, [currentStep]);

  const handleSave = useCallback(async () => {
    if (
      state.locationId === '' ||
      state.sessionDate === '' ||
      uploadFile == null ||
      preview == null
    ) {
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await PhysicalCountService.sessionsImportAndFinalize(
        Number(state.locationId),
        state.sessionDate,
        uploadFile,
        UPLOAD_PARSE_MODE,
        UPLOAD_NORMALIZATION,
      );
      onSuccess?.();
      handleClose();
    } catch (err: unknown) {
      setSaveError(extractErrorMessage(err, 'No se pudo guardar la sesión'));
    } finally {
      setSaving(false);
    }
  }, [state.locationId, state.sessionDate, uploadFile, preview, onSuccess, handleClose]);

  const isNextDisabled = currentStep === 1 && (state.locationId === '' || state.sessionDate === '');

  const isSaveDisabled =
    saving || previewLoading || uploadFile == null || preview == null || state.locationId === '';

  return {
    currentStep,
    totalSteps,
    state,
    updateState,
    handleClose,
    handleCloseClick,
    handleConfirmClose,
    handleCancelClose,
    showCloseConfirmation,
    handleNextStep,
    handlePreviousStep,
    handleSave,
    isNextDisabled,
    isSaveDisabled,
    saving,
    saveError,
    previewLoading,
    uploadFile,
    setUploadFile,
    preview,
    fileInputKey,
  };
};
